// Import math.js with tree-shaking to reduce size
import {
    create,
    evaluateDependencies,
    addDependencies,
    subtractDependencies,
    divideDependencies,
    multiplyDependencies,
    powDependencies,
    absDependencies,
    modDependencies,
    sqrtDependencies,
    roundDependencies,
    floorDependencies,
    ceilDependencies,
    andDependencies,
    orDependencies,
    notDependencies,
    equalDependencies,
    unequalDependencies,
    largerDependencies,
    largerEqDependencies,
    smallerDependencies,
    smallerEqDependencies,
    concatDependencies,
} from 'mathjs';

const math = create({
    evaluateDependencies,
    addDependencies,
    subtractDependencies,
    divideDependencies,
    multiplyDependencies,
    powDependencies,
    absDependencies,
    modDependencies,
    sqrtDependencies,
    roundDependencies,
    floorDependencies,
    ceilDependencies,
    andDependencies,
    orDependencies,
    notDependencies,
    equalDependencies,
    unequalDependencies,
    largerDependencies,
    largerEqDependencies,
    smallerDependencies,
    smallerEqDependencies,
    concatDependencies,
});

// Add custom functions to work in math.evaluate()
// Anything added/changed here should also be done in the API's utils/math.js as well
const dateFormat = require('dateformat');
math.import({
    wordCount(text) {
        return text.split(/[^a-z0-9'_-]+/i).filter(v => v !== '').length;
    },
    if(cond, pass = 1, fail = 0) {
        return cond == 0 ? fail : pass;
    },
    date(timestamp, format = 'yyyy-mm-dd') {
        if (!timestamp) return '';
        // dateformat treats dates w/o a time as UTC, but this messes up day of week output which will still be
        // the day before in EST for that date, so this fixes that
        if (typeof timestamp === 'string' && !/(:|[ap]\.?m)/i.test(timestamp)) {
            timestamp = timestamp.trim() + ' 00:00:00';
        }
        return dateFormat(timestamp, format);
    },
    fixed(number, digits = 2) {
        if (!number) {
            number = 0;
        } else if (typeof number !== 'number') {
            number = parseFloat(number);
        }
        let mult = Math.pow(10, digits);
        number = Math.round(number * mult) / mult;
        return number.toFixed(digits);
    },
    now() {
        return Date.now();
    },
    timestamp,
    secondsUntil,
    minutesUntil,
    hoursUntil,
    daysUntil,
    weeksUntil: (date, referenceDate = null) => daysUntil(date, referenceDate) / 7,
    monthsUntil: (date, referenceDate = null) => daysUntil(date, referenceDate) / 30,
    yearsUntil: (date, referenceDate = null) => daysUntil(date, referenceDate) / 365,
});

function timestamp(date) {
    if (!date) return 0;
    // Add a dummy date to allow parsing time chooser values
    if (typeof date === 'string' && /^\d\d?:\d\d/.test(date)) {
        date = `Jan 1 1970 ${date}`;
    }
    return (new Date(date)).getTime();
}

function secondsUntil(date, referenceDate = null) {
    if (!date) return 0;
    const time = timestamp(date);
    const ref = referenceDate ? timestamp(referenceDate) : Date.now();
    return (time - ref) / 1000;
}

function minutesUntil(date, referenceDate = null) {
    return secondsUntil(date, referenceDate) / 60;
}

function hoursUntil(date, referenceDate = null) {
    return minutesUntil(date, referenceDate) / 60;
}

function daysUntil(date, referenceDate = null) {
    if (!referenceDate) referenceDate = dateFormat('yyyy-mm-dd');
    return hoursUntil(date, referenceDate) / 24;
}

export default math;